import React, { useState, useCallback, useEffect } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, getSortedRowModel, PaginationState, getPaginationRowModel } from "@tanstack/react-table";
import { Table, Space, Pagination } from '@mantine/core';

/** 
* Definition of columns for the table 
* Property names should match the "accessor" in the table columns
*/
export interface PatentDocketTableColumns { 
    tmTitle: string;
    grantingNumber: string;
    applicationNumber: string;
    action: string;
    description: string;
    daysRemaining: JSX.Element;
    dueDate: string;
};

/** Props for the table component */
export interface ITableProps {
    data: PatentDocketTableColumns[];
    pageSize?: number;
}

export default function PatentDocketTable(props: ITableProps) {
    const [pageIndexOneIndexed, setPageIndexOneIndexed] = useState<number>(1);
    const [minYear, setMinYear] = useState<number>(0);
    const [maxYear, setMaxYear] = useState<number>(0);
    const [currentYear, setCurrentYear] = useState<number>(0);

    const getMinMaxYears = (data: PatentDocketTableColumns[]): { minYear: number, maxYear: number } => {
        // Get last 4 characters of due date...
        const getYear = (dueDate: string): number => {
            return parseInt(dueDate.substr(dueDate.length - 4));
        }

        const years = data.map(row => getYear(row.dueDate));
        return { minYear: Math.min(...years), maxYear: Math.max(...years) };
    }

    const { data } = props;
    const pageSize = props.pageSize ?? 20;
    const columnHelper = createColumnHelper<PatentDocketTableColumns>();
    const columns = [
        columnHelper.accessor("tmTitle", { header: "Title" }),
        columnHelper.accessor("grantingNumber", { header: "Granting Number" }),
        columnHelper.accessor("applicationNumber", { header: "Application Number" }),
        columnHelper.accessor("action", { header: "Action" }),
        columnHelper.accessor("description", { header: "Description" }),
        columnHelper.accessor("daysRemaining", { header: "Days Remaining" }),
        columnHelper.accessor("dueDate", { header: "Due Date" }),
    ];

    // Filter data so that only the current year is shown...
    const tableData = data.filter((x) => {
        return x.dueDate.indexOf(currentYear.toString()) > -1;
    });

    // When data changes, update the years...
    useEffect(() => {
        const { minYear, maxYear } = getMinMaxYears(data);
        setMinYear(minYear);
        setMaxYear(maxYear);
        setCurrentYear(minYear);
    }, [data]);

    const table = useReactTable({ 
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageIndex: 0, pageSize: pageSize } }
    });

    const onPageChange = useCallback((idxOneIndexed: number) => {
        table.setPageIndex(idxOneIndexed - 1);
        setPageIndexOneIndexed(idxOneIndexed);
        setCurrentYear(minYear + idxOneIndexed - 1);
    }, [table, minYear, setPageIndexOneIndexed, setCurrentYear]);

    return (
        <>
        <Table className="DocketTable">
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                    <th key={header.id}>
                        {header.isPlaceholder
                            ? null
                            : <div style={{ cursor: "pointer" }} onClick={header.column.getToggleSortingHandler()}>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                        }
                    </th>
                    ))}
                </tr>
            ))}
            </thead>

            <tbody>
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map((cell, index) => {
                            const style: any = {};
                            return (
                                <td key={cell.id} style={style}>
                                    {cell.renderValue() as JSX.Element}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </Table>
        <Space h="md" />
        <Pagination page={pageIndexOneIndexed} 
            onChange={onPageChange} 
            total={maxYear - minYear + 1} 
        />
        </>
    );
}